import api from './api';

export interface Reserva {
  id: string;
  sala_id: string;
  usuario_id: string;
  titulo: string;
  descricao?: string;
  data_inicio: string;
  data_fim: string;
  status: 'ativa' | 'cancelada' | 'concluida';
  participantes?: string[];
  observacoes?: string;
  data_criacao: string;
  data_cancelamento?: string;
  cancelada_por?: string;
  motivo_cancelamento?: string;
  // Dados relacionados (joins)
  sala_nome?: string;
  sala_tipo?: string;
  sala_cor?: string;
  sala_capacidade?: number;
  usuario_nome?: string;
  usuario_email?: string;
  cancelada_por_nome?: string;
}

export interface CriarReservaDTO {
  sala_id: string;
  titulo: string;
  descricao?: string;
  data_inicio: string;
  data_fim: string;
  participantes?: string[];
  observacoes?: string;
}

export interface AtualizarReservaDTO {
  titulo?: string;
  descricao?: string;
  data_inicio?: string;
  data_fim?: string;
  participantes?: string[];
  observacoes?: string;
}

export interface FiltrosReserva {
  sala_id?: string;
  usuario_id?: string;
  data_inicio?: string;
  data_fim?: string;
  status?: 'ativa' | 'cancelada' | 'concluida';
}

/**
 * Listar reservas com filtros
 */
export const listarReservas = async (filtros?: FiltrosReserva): Promise<Reserva[]> => {
  const response = await api.get('/reservas', { params: filtros });
  return response.data;
};

/**
 * Listar minhas reservas
 */
export const listarMinhasReservas = async (status?: 'ativa' | 'cancelada' | 'concluida'): Promise<Reserva[]> => {
  const response = await api.get('/reservas/minhas', {
    params: status ? { status } : undefined
  });
  return response.data;
};

/**
 * Obter reservas de uma sala para calendário
 */
export const obterCalendarioSala = async (
  salaId: string,
  mes?: number,
  ano?: number
): Promise<Reserva[]> => {
  const params: any = {};
  if (mes) params.mes = mes;
  if (ano) params.ano = ano;
  
  const response = await api.get(`/reservas/calendario/${salaId}`, { params });
  return response.data;
};

/**
 * Obter detalhes de uma reserva
 */
export const obterReserva = async (id: string): Promise<Reserva> => {
  const response = await api.get(`/reservas/${id}`);
  return response.data;
};

/**
 * Criar nova reserva
 */
export const criarReserva = async (dados: CriarReservaDTO): Promise<Reserva> => {
  const response = await api.post('/reservas', dados);
  return response.data;
};

/**
 * Atualizar reserva
 */
export const atualizarReserva = async (id: string, dados: AtualizarReservaDTO): Promise<Reserva> => {
  const response = await api.put(`/reservas/${id}`, dados);
  return response.data;
};

/**
 * Cancelar reserva
 */
export const cancelarReserva = async (id: string, motivo?: string): Promise<void> => {
  await api.delete(`/reservas/${id}`, {
    data: { motivo }
  });
};

/**
 * Concluir reserva
 */
export const concluirReserva = async (id: string): Promise<void> => {
  await api.post(`/reservas/${id}/concluir`);
};

/**
 * Verificar se há conflito de horário
 */
export const verificarConflito = async (
  salaId: string,
  dataInicio: string,
  dataFim: string,
  reservaId?: string
): Promise<boolean> => {
  try {
    const filtros: FiltrosReserva = {
      sala_id: salaId,
      data_inicio: dataInicio,
      data_fim: dataFim,
      status: 'ativa'
    };
    
    const reservas = await listarReservas(filtros);
    
    // Verificar conflitos manualmente
    const inicio = new Date(dataInicio);
    const fim = new Date(dataFim);
    
    for (const reserva of reservas) {
      if (reservaId && reserva.id === reservaId) continue;
      
      const reservaInicio = new Date(reserva.data_inicio);
      const reservaFim = new Date(reserva.data_fim);
      
      // Verificar sobreposição
      if (
        (inicio >= reservaInicio && inicio < reservaFim) ||
        (fim > reservaInicio && fim <= reservaFim) ||
        (inicio <= reservaInicio && fim >= reservaFim)
      ) {
        return true; // Há conflito
      }
    }
    
    return false; // Sem conflito
  } catch (error) {
    console.error('Erro ao verificar conflito:', error);
    return false;
  }
};

export default {
  listarReservas,
  listarMinhasReservas,
  obterCalendarioSala,
  obterReserva,
  criarReserva,
  atualizarReserva,
  cancelarReserva,
  concluirReserva,
  verificarConflito
};
